
import { DefectModel, DefectWorkplaceModel, QueryOperator } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref } from 'vue-property-decorator';
import { defectsStore } from '@/libs/defects/+state/store';
import { defectWorkplacesStore } from '@/libs/defects/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import DefectSelectDialog from './DefectSelectDialog.vue';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

@Component({
    components: { DefectSelectDialog },
})
export default class WorkplaceDefectsList extends Page {
    @Prop() workplaceId!: string;
    @Prop({ default: false }) compact!: boolean;
    @Ref() selectDialog!: any;

    loading = false;

    mounted() {
        this.loadData();
    }

    loadData() {
        this.loading = true;
        defectWorkplacesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(undefined, [{ field: 'workplaceId', op: QueryOperator.Eq, comparand: this.workplaceId }])
        );
        this.subscribe(defectWorkplacesStore, CrudReponse.GetAll)
            .then(() => {
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
            });
    }

    get defectWorkplaces(): DefectWorkplaceModel[] {
        const items = (defectWorkplacesStore.useGetter(CrudGetter.Data) as DefectWorkplaceModel[]) ?? [];
        return items.filter((x) => x.workplaceId === this.workplaceId);
    }

    get sortedDefectWorkplaces(): DefectWorkplaceModel[] {
        return [...this.defectWorkplaces].sort((a, b) => {
            const defectA = this.getDefect(a.defectId);
            const defectB = this.getDefect(b.defectId);
            return (defectA?.code ?? '').localeCompare(defectB?.code ?? '');
        });
    }

    get allDefects(): DefectModel[] {
        return (defectsStore.useGetter(CrudGetter.Data) as DefectModel[]) ?? [];
    }

    getDefect(defectId: string | undefined): DefectModel | undefined {
        if (!defectId) return undefined;
        return this.allDefects.find((x) => x.id === defectId);
    }

    get currentDefectIds(): string[] {
        return this.defectWorkplaces.map(dw => dw.defectId!);
    }

    openSelectDialog() {
        this.selectDialog.open(this.currentDefectIds);
    }

    onDefectsSelected(selectedDefects: DefectModel[]) {
        const selectedIds = selectedDefects.map(d => d.id!);
        const currentIds = this.currentDefectIds;

        // Najít nově přidané
        const toAdd = selectedDefects.filter(d => !currentIds.includes(d.id!));
        
        // Najít odebrané
        const toRemove = this.defectWorkplaces.filter(dw => !selectedIds.includes(dw.defectId!));

        const promises: Promise<any>[] = [];

        // Přidat nové poruchy
        toAdd.forEach((defect) => {
            const defectWorkplace: DefectWorkplaceModel = {
                id: Guid.EmptyGuid(),
                defectId: defect.id,
                workplaceId: this.workplaceId,
            };
            const promise = new Promise((resolve, reject) => {
                defectWorkplacesStore.dispatch(CrudAction.Create, { item: defectWorkplace });
                this.subscribe(defectWorkplacesStore, CrudReponse.Create)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        // Odebrat poruchy
        toRemove.forEach((defectWorkplace) => {
            const promise = new Promise((resolve, reject) => {
                defectWorkplacesStore.dispatch(CrudAction.Delete, { id: defectWorkplace.id });
                this.subscribe(defectWorkplacesStore, CrudReponse.Delete)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        Promise.all(promises).then(() => {
            this.loadData();
        });
    }
}
